import {
  Badge,
  Box,
  Button,
  CloseIcon,
  Column,
  HtImage,
  IconButton,
  Row,
  Skeleton,
  SkeletonBox,
  Text,
  TextWithTooltip,
} from "@hightouch/htui";

import { abbreviateNumber } from "src/utils/numbers";

import { ASSET_CARD_ASPECT_RATIO } from "src/components/asset-picker/constants";

// White matte (px) inset above and below the image, behind the frosted
// header/footer overlays: raw image pixels alias against the backdrop blur and
// the card's rounded corners, so a clean (clippable) element forms the edges
// instead — the same treatment as the email preview matte in candidate-card.
const ASSET_MEDIA_MATTE = 5;

/**
 * A left-rail entry in an asset picker: an icon, label, and a compact count
 * badge (skeleton while the count loads). Active state renders as a constant-
 * width border + tinted background so toggling can't shift the layout.
 */
export const RailItem = ({
  label,
  icon: Icon,
  iconUrl,
  count,
  isLoading = false,
  isActive,
  onClick,
}: {
  label: string;
  icon: React.ElementType;
  /** An image drawn in place of `icon`. */
  iconUrl?: string | null;
  count?: number;
  // While the source's count query is loading, the badge shows a skeleton.
  isLoading?: boolean;
  isActive: boolean;
  onClick: () => void;
}) => (
  <Row
    as="button"
    type="button"
    align="center"
    justify="space-between"
    gap={2}
    px={3}
    py={2}
    borderRadius="lg"
    // Constant-width border so toggling active can't shift the layout.
    borderWidth="hairline"
    borderStyle="solid"
    borderColor={isActive ? "primary.border" : "transparent"}
    cursor="pointer"
    bg={isActive ? "primary.background" : "transparent"}
    color={isActive ? "primary.text" : "text.primary"}
    _hover={{ bg: isActive ? "primary.background" : "base.lightBackground" }}
    onClick={onClick}
  >
    <Row align="center" gap={2} minWidth={0}>
      {iconUrl ? (
        <HtImage
          src={iconUrl}
          boxSize={5}
          flexShrink={0}
          objectFit="contain"
          decorative
        />
      ) : (
        <Box
          as={Icon}
          boxSize={5}
          flexShrink={0}
          color={isActive ? "primary.text" : "text.secondary"}
        />
      )}
      <Box overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis">
        <Text color="inherit">{label}</Text>
      </Box>
    </Row>
    {/* Compact notation ("65.5K") — catalogs can run to 100k+ products, which
        would otherwise truncate the badge and wrap the label. */}
    {count !== undefined && (
      <Box flexShrink={0}>
        <Skeleton isLoading={isLoading}>
          <Badge size="sm">{abbreviateNumber(count)}</Badge>
        </Skeleton>
      </Box>
    )}
  </Row>
);

export const AssetGrid = ({
  children,
  columns = 4,
}: {
  children: React.ReactNode;
  /** Cards per row. Drop to 3 when a preview panel narrows the grid area. */
  columns?: number;
}) => (
  <Box
    display="grid"
    gridTemplateColumns={`repeat(${columns}, minmax(0, 1fr))`}
    gap={4}
    // Breathing room so the cards' 2px selection outline isn't clipped by the
    // scroll container.
    p="2px"
  >
    {children}
  </Box>
);

export const AssetCardFooter = ({
  title,
  subtitle,
}: {
  title: string;
  subtitle?: string;
}) => (
  <>
    <TextWithTooltip size="sm" fontWeight="medium">
      {title}
    </TextWithTooltip>
    {subtitle && (
      <TextWithTooltip size="sm" color="text.secondary">
        {subtitle}
      </TextWithTooltip>
    )}
  </>
);

export const AssetCardMedia = ({
  children,
  matteTop = true,
  matteBottom = true,
}: {
  children: React.ReactNode;
  /**
   * Whether to inset the white matte on each edge. The matte only earns its
   * keep behind a frosted overlay — drop the top matte on single-select cards
   * that render no header, where it otherwise reads as a stray white bar above
   * the image.
   */
  matteTop?: boolean;
  matteBottom?: boolean;
}) => (
  <Box
    width="100%"
    height="100%"
    overflow="hidden"
    bg="white"
    borderRadius="2xl"
    borderTop={matteTop ? `${ASSET_MEDIA_MATTE}px solid` : undefined}
    borderBottom={matteBottom ? `${ASSET_MEDIA_MATTE}px solid` : undefined}
    borderColor="white"
  >
    {children}
  </Box>
);

// A small portrait preview of a selected asset — the modal card's proportions
// at roughly 40% scale — with an in-place remove button. Used by the intake
// form's selection rows (products, images, templates) beneath their pickers.
// Omit `onRemove` for read-only surfaces (the campaign context view); the
// remove button is dropped entirely.
export const AssetPreviewCard = ({
  onRemove,
  children,
}: {
  onRemove?: () => void;
  children: React.ReactNode;
}) => (
  <Box
    position="relative"
    width="70px"
    height="90px"
    borderRadius="md"
    borderWidth="hairline"
    borderStyle="solid"
    borderColor="base.border"
    overflow="hidden"
    bg="base.lightBackground"
    flexShrink={0}
  >
    {children}
    {onRemove && (
      <Box position="absolute" top={0} right={0.5}>
        <IconButton
          aria-label="Remove asset"
          icon={CloseIcon}
          size="2xs"
          variant="secondary"
          shape="circle"
          onClick={onRemove}
        />
      </Box>
    )}
  </Box>
);

// Read-only card body for a stored selection whose asset was deleted (or
// archived) after it was picked. `label` is the full message, e.g. "Product no
// longer exists". Fills the parent card by default; pass `height` when the
// parent has no fixed height of its own (e.g. the template grid, where the
// email preview normally sets it).
export const MissingAssetPlaceholder = ({
  label,
  height = "100%",
}: {
  label: string;
  height?: string | number;
}) => (
  <Column
    align="center"
    justify="center"
    width="100%"
    height={height}
    p={1}
    bg="base.lightBackground"
    textAlign="center"
  >
    <Text size="sm" color="text.tertiary">
      {label}
    </Text>
  </Column>
);

export const NoImageFallback = () => (
  <Column
    align="center"
    justify="center"
    width="100%"
    height="100%"
    bg="base.lightBackground"
  >
    <Text size="sm" color="text.tertiary">
      No image
    </Text>
  </Column>
);

export const AssetGridSkeleton = ({ columns = 4 }: { columns?: number }) => (
  <AssetGrid columns={columns}>
    {Array.from({ length: 8 }).map((_, index) => (
      <Skeleton key={index} isLoading={true}>
        <SkeletonBox
          width="100%"
          sx={{ aspectRatio: ASSET_CARD_ASPECT_RATIO }}
          borderRadius="2xl"
        />
      </Skeleton>
    ))}
  </AssetGrid>
);

export const LoadMoreFooter = ({
  shownCount,
  totalCount,
  isTotalCountCapped = false,
  hasMore,
  isLoadingMore,
  onLoadMore,
  label,
}: {
  shownCount: number;
  totalCount?: number;
  isTotalCountCapped?: boolean;
  hasMore: boolean;
  isLoadingMore: boolean;
  onLoadMore: () => void;
  label: string;
}) => (
  <Row align="center" justify="center" gap={4} py={2}>
    <Text size="sm" color="text.secondary">
      {totalCount === undefined
        ? `Showing ${shownCount} ${label}`
        : isTotalCountCapped
          ? hasMore
            ? `Showing ${shownCount} of the first ${totalCount} ${label}`
            : `Showing the first ${totalCount} ${label}`
          : `Showing ${shownCount} of ${totalCount} ${label}`}
    </Text>
    {hasMore && (
      <Button size="sm" isLoading={isLoadingMore} onClick={onLoadMore}>
        Load more
      </Button>
    )}
  </Row>
);
